import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgSelectModule } from "@ng-select/ng-select";
import { isEmpty } from 'lodash';
import { UserService } from '../../core/services/user.service';
import { LocalStorageService } from 'ngx-webstorage';
import { ToastrService } from 'ngx-toastr';
import { CookieService } from '../../core/services/cookie.service';
import { BackendService } from '../../core/services/backend.service';
import { Automation } from '../../core/interfaces/Automation';
import { SpaceFieldOption, SpaceType, SpaceTypeField } from '../../core/interfaces/SpaceType';
import { switchMap } from 'rxjs';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { NgxTgModule, TranslateService } from '@worthy-ventures/metaglotta-ngx';

@Component({
  selector: 'app-journey',
  imports: [CommonModule, NgSelectModule, FormsModule, ReactiveFormsModule, LottieComponent, NgxTgModule],
  templateUrl: './journey.component.html',
  styleUrl: './journey.component.scss'
})
export class JourneyComponent {
  currentStep = 1; //Space Type selection

  //Fetched from backend
  automations: Automation[] = [];
  spaceTypes: SpaceType[] = [];
  userCountryCodeFromIP: string = '';
  countries: any[] = [];
  estimatedPrice: any = null;
  /**
   * Only sent for space types that are priced automatically ('Home'). Anything
   * else comes back as a "we'll be in touch" sentence, which must not get a
   * currency appended.
   */
  estimatedCurrency: string | null = null;

  //Selected options and user entered data
  selectedSpaceType: SpaceType | null = null;
  selectedSize: number | null = null;
  selectedAutomations: Automation[] = [];
  selectedCountryCode: any | null = null;
  phoneNumber: string | null = null;
  email: string | null = null;

  // OTP Verification
  otpForm!: FormGroup;
  submittedOtpCode: string = '';
  invalidCode: boolean = false;
  // Only a successful /login/verify grants a Parse session, which the backend
  // needs to attach the required `user` pointer on the order.
  otpVerified: boolean = false;
  isVerifying: boolean = false;
  userId: string = '';

  // Timers for otp
  timer: any; // To store the interval reference
  resendTimer: any;
  remainingTime: number = 120; // 2 minutes in seconds
  displayTime: string = '02:00'; // Display format
  resendRemainingTime: number = 25; // 25 seconds
  resendDisplayTime: string = '00:25'; // Display format

  @ViewChildren('input') otpInput!: QueryList<ElementRef>;

  /* ---- space detail fields ---------------------------------------------- *
   * Step 2's questions come from the selected space type's `fields`, so the set
   * differs per type and changing it is a data edit rather than a release. The
   * fixed 1-5/5-10/11+ room band that used to live here is now just another
   * field (`rooms` on Office and Hotel; Home asks bedrooms and living areas
   * instead), which is why it is gone.
   */

  /** Answers keyed by field key — sent as the order's `spaceDetails`. */
  spaceDetails: Record<string, string | number | null> = {};

  /** The selected type's fields in render order. `order` is authoritative, not
   *  array position, so it is sorted here rather than trusted from the API. */
  get orderedFields(): SpaceTypeField[] {
    return [...(this.selectedSpaceType?.fields ?? [])].sort((a, b) => a.order - b.order);
  }

  /**
   * The options each dropdown offers, keyed by field key.
   *
   * A dropdown gets them one of two ways: listed explicitly in the collection
   * when the choices are bands or text ("1–5", "500+"), or generated here from
   * numeric bounds when they are just consecutive numbers. The second form is
   * why a 0-100 range is three numbers in the database rather than 101 rows.
   *
   * Cached per selected space type rather than computed in the template: a
   * method call in `[items]` hands ng-select a fresh array on every change
   * detection pass, which makes it rebuild its list.
   *
   * Values are optional because the map is genuinely sparse: it is `{}` until
   * resetSpaceDetails() fills it, and the template renders against it before
   * that. Declaring it as always-present made the compiler read the `?? []` in
   * the template as dead code (NG8102) while the miss it guards is real — the
   * same miss the `?.` reads below already allow for.
   */
  fieldOptions: Record<string, SpaceFieldOption[] | undefined> = {};

  private optionsFor(field: SpaceTypeField): SpaceFieldOption[] {
    const o = field.inputOptions;
    if (o.options?.length) return o.options;
    if (o.input !== 'dropdown') return [];

    // Numbers are the same in every language, so a generated range needs no
    // translation — which is the other reason this form is preferable.
    const step = o.step && o.step > 0 ? o.step : 1;
    const min = o.min ?? 0;
    const max = o.max ?? min;
    const out: SpaceFieldOption[] = [];
    for (let n = min; n <= max; n += step) {
      out.push({ value: String(n), label: String(n) });
    }
    return out;
  }

  /** Seed every field so a stepper starts at its configured default rather than
   *  at a blank the user has to touch before it reads as answered. */
  private resetSpaceDetails(): void {
    this.spaceDetails = {};
    this.fieldOptions = {};
    for (const field of this.orderedFields) {
      this.fieldOptions[field.key] = this.optionsFor(field);
    }

    for (const field of this.orderedFields) {
      const { input, default: dflt } = field.inputOptions;

      if (input === 'stepper') {
        this.spaceDetails[field.key] = dflt ?? field.inputOptions.min ?? 0;
      } else if (input === 'dropdown') {
        // Opens on a real value rather than the "Select" placeholder. The
        // first option, not a literal 0: only the counts that used to be
        // steppers have a zero — the rest start at 1 or at a range like
        // "1–5". `default` in the data wins if a field wants another.
        const first = this.fieldOptions[field.key]?.[0]?.value ?? null;
        this.spaceDetails[field.key] = dflt !== undefined ? String(dflt) : first;
      } else {
        this.spaceDetails[field.key] = null;
      }
    }
  }

  /** Move a stepper by one step, clamped to its configured bounds. */
  stepField(field: SpaceTypeField, direction: 1 | -1): void {
    const { min = 0, max = 99, step = 1 } = field.inputOptions;
    const current = Number(this.spaceDetails[field.key] ?? min);
    this.spaceDetails[field.key] = Math.min(max, Math.max(min, current + direction * step));
  }

  stepperValue(field: SpaceTypeField): number {
    return Number(this.spaceDetails[field.key] ?? field.inputOptions.min ?? 0);
  }

  atMin(field: SpaceTypeField): boolean {
    return this.stepperValue(field) <= (field.inputOptions.min ?? 0);
  }

  atMax(field: SpaceTypeField): boolean {
    return this.stepperValue(field) >= (field.inputOptions.max ?? Infinity);
  }

  /** Pick an option. Tapping the selected one again clears it, so a mis-tap is
   *  recoverable without a separate "clear" control. */
  selectFieldOption(field: SpaceTypeField, value: string): void {
    this.spaceDetails[field.key] = this.spaceDetails[field.key] === value ? null : value;
  }

  /** True once every field marked `required` has an answer.
   *
   *  Steppers go through `stepperValue()` rather than reading the raw state, so
   *  validation always agrees with the number on screen. Reading the state
   *  directly meant an unseeded field showed 0 but validated as `undefined`,
   *  blocking Next with nothing on screen to explain why.
   *
   *  A required stepper has to be above zero. Counting the seeded default as an
   *  answer would make `required` do nothing at all, since every stepper starts
   *  with a value — so a count that may legitimately be zero should simply not
   *  be marked required in the collection. */
  private requiredFieldsAnswered(): boolean {
    return this.orderedFields.every(field => {
      if (!field.required) return true;
      if (field.inputOptions.input === 'stepper') return this.stepperValue(field) > 0;
      const value = this.spaceDetails[field.key];
      return value !== null && value !== undefined && value !== '';
    });
  }

  /** The answers as sent to the backend. Steppers resolve through
   *  `stepperValue()` so an untouched one is sent as the number the user saw
   *  rather than dropping out of the payload. */
  private collectSpaceDetails(): Record<string, string | number | null> {
    return this.orderedFields.reduce<Record<string, string | number | null>>((acc, field) => {
      acc[field.key] = field.inputOptions.input === 'stepper'
        ? this.stepperValue(field)
        : this.spaceDetails[field.key] ?? null;
      return acc;
    }, {});
  }

  /** Label/value pairs for the step 6 summary, with select values resolved back
   *  to their option label. Empty answers are dropped rather than shown blank. */
  get spaceDetailSummary(): { label: string; value: string; isText: boolean }[] {
    return this.orderedFields.reduce<{ label: string; value: string; isText: boolean }[]>((rows, field) => {
      const raw = field.inputOptions.input === 'stepper'
        ? this.stepperValue(field)
        : this.spaceDetails[field.key];
      if (raw === null || raw === undefined || raw === '') return rows;

      let value = String(raw);
      // A dropdown stores its option id, same as the band did, so the summary
      // has to resolve it back to the label.
      if (field.inputOptions.input === 'select' || field.inputOptions.input === 'dropdown') {
        const match = this.fieldOptions[field.key]?.find(o => o.value === raw);
        if (match) value = match.label;
      }

      // The form asks a question ("Tell us more about your space"); a summary
      // wants the field's name. Flagged rather than swapped here so the
      // template can use the translate pipe — `instant()` appends Metaglotta's
      // invisible in-context markers while editing is armed, which have no
      // business in a label.
      rows.push({ label: field.label, value, isText: field.inputOptions.input === 'text' });
      return rows;
    }, []);
  }

  isMobileView = window.innerWidth <= 992;

  lottieOptions: AnimationOptions = {
    path: '/assets/lottie.json',
    loop: false,
    autoplay: true
  };

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private userService: UserService,
    private ls: LocalStorageService,
    private toastr: ToastrService,
    private cd: ChangeDetectorRef,
    private cookieService: CookieService,
    private backendService: BackendService,
    private translate: TranslateService,
  ) { }

  ngOnInit() {
    this.otpForm = this.fb.group({
      code1: ['', Validators.required],
      code2: ['', Validators.required],
      code3: ['', Validators.required],
      code4: ['', Validators.required],
    });

    // Fetch space types
    this.backendService.getSpaceTypes().subscribe({
      next: (response) => {
        this.spaceTypes = response.data;
      },
      error: (err) => {
        console.error('Error fetching space types:', err);
      }
    });

    // Fetch automations
    this.backendService.getAutomations().subscribe({
      next: (response) => {
        this.automations = response.data;
      },
      error: (err) => {
        console.error('Error fetching automations:', err);
      }
    });

    //Fetch country codes and detect user country from IP
    this.backendService.getCountryCodes().pipe(
      switchMap((response) => {
        this.countries = response.countries;

        //map the countries and add a '+' sign before the phone code if not already present
        this.countries = this.countries.map(c => {
          if (!c.phonePrefix.startsWith('+') && !c.phonePrefix.startsWith('00')) {
            return { ...c, phonePrefix: '+' + c.phonePrefix };
          }
          return c;
        });
        return this.backendService.getCountryFromIP();
      })
    ).subscribe({
      next: (response) => {
        this.userCountryCodeFromIP = response.data || '';
        if (this.userCountryCodeFromIP.length) {
          this.selectedCountryCode = this.countries.find(c => c.countryCode === this.userCountryCodeFromIP);
        }
      },
      error: (err) => {
        console.error('Error:', err);
      }
    });

  }

  ngOnDestroy() {
    this.clearTimers();
  }

  public selectSpace(spaceType: SpaceType) {
    // Switching type changes which questions step 2 asks, so previous answers
    // would be keyed to fields that are no longer on screen.
    const changed = this.selectedSpaceType !== spaceType;
    this.selectedSpaceType = spaceType;
    if (changed) this.resetSpaceDetails();
  }

  public selectAutomation(automation: Automation) {
    const index = this.selectedAutomations.indexOf(automation);
    if (index > -1) {
      this.selectedAutomations.splice(index, 1);
    } else {
      this.selectedAutomations.push(automation);
    }
  }

  /**
   * "16000 EUR" for a priced space type; the bare backend sentence for the ones
   * sales quotes by hand, which has no currency to show.
   */
  get estimatedPriceLabel(): string {
    if (this.estimatedPrice === null || this.estimatedPrice === undefined) return '';
    return this.estimatedCurrency
      ? `${this.estimatedPrice} ${this.estimatedCurrency}`
      : `${this.estimatedPrice}`;
  }

  public goToStep(step: number) {
    this.currentStep = step;
    if (step === 0) {
      this.router.navigate(['/']);
    }

    //Phone verification step
    if (step === 5) {
      // A fresh code is sent, so any earlier verification no longer counts
      this.otpVerified = false;
      this.sendOTP(false);
      this.startTimer();
      this.startResendTimer();
    }

    //Order summary step
    if (step === 6) {
      if (this.selectedSize && this.selectedSpaceType) {
        this.backendService.calculatePrice(this.selectedSize, this.selectedSpaceType).subscribe(
          {
            next: (response) => {
              this.estimatedPrice = response.data.price;
              this.estimatedCurrency = response.data.currency ?? null;
            },
            error: (err) => {
              this.estimatedPrice = null;
              this.estimatedCurrency = null;
              this.toastr.error(err.error?.message || this.translate.instant('journey_toast_price_failed'));
            }
          }
        )
      }
    }
  }

  public disableNextButton(): boolean {
    if (this.currentStep === 1 && !this.selectedSpaceType) {
      return true;
    } else if (this.currentStep === 2 && (!this.selectedSize || !this.requiredFieldsAnswered())) {
      return true;
    } else if (this.currentStep === 3 && this.selectedAutomations.length === 0) {
      return true;
    } else if (this.currentStep === 4) {
      const phoneValid = this.phoneNumber && this.phoneNumber.toString().length === 10;
      const emailValid = this.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email);
      if (!phoneValid || !emailValid || !this.selectedCountryCode) {
        return true;
      }
    } else if (this.currentStep === 5) {
      return !this.otpForm.valid;
    }
    return false;
  }

  onInput(index: number) {
    // Check if the current input has a value and move to the next input
    const val = this.otpForm.get(`code${index + 1}`)?.value;
    if (!isEmpty(val) && !isNaN((Number(val))) && val > -1) {
      if (index < 3) {
        this.otpInput.toArray()[index + 1].nativeElement.focus();
      }
      if (index === 3 && this.otpForm.valid) {
        this.submitOTPForm();
      }
    }
  }
  onKeyDown(event: KeyboardEvent, index: number) {
    // Handle backspace to move to the previous input
    if (event.key === 'Backspace' && index > 0 && !this.otpForm.get(`code${index + 1}`)?.value) {
      this.otpInput.toArray()[index - 1].nativeElement.focus();
      return;
    }
  }

  validateInput(event: KeyboardEvent) {
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'Backspace'];
    // Prevent any key that is not a digit or backspace
    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  submitOTPForm() {

    const c1 = this.otpForm.value.code1;
    const c2 = this.otpForm.value.code2;
    const c3 = this.otpForm.value.code3;
    const c4 = this.otpForm.value.code4;
    this.submittedOtpCode = c1.toString() + c2.toString() + c3.toString() + c4.toString();

    if (typeof c1 === 'undefined' || typeof c2 === 'undefined' || typeof c3 === 'undefined' || typeof c4 === 'undefined') {
      return;
    }

    if (this.isVerifying) {
      return;
    }
    this.invalidCode = false;
    this.isVerifying = true;

    this.userService.verifyCode({
      "code": this.submittedOtpCode,
      "userId": this.userId || this.ls.retrieve('uid'),
      "systemInfo": {
        "deviceType": "web",
        "appVersion": 1
      }
    }).subscribe({
      next: (response) => {
        this.isVerifying = false;

        if (response.user) {
          this.ls.store('uid', response.user);
        }
        this.ls.store('session_token', response.token);
        this.otpVerified = true;
        this.clearTimers();
        this.toastr.success(this.translate.instant('journey_toast_phone_verified'));

        this.goToStep(this.currentStep + 1);
        this.cookieService.setCookie('phoneNumber', this.selectedCountryCode.code + ' ' + this.phoneNumber, 30);
      },
      error: (err) => {
        this.isVerifying = false;
        this.invalidCode = true;
        this.otpVerified = false;
        this.toastr.error(err.error?.message || this.translate.instant('journey_toast_otp_failed'));
      }
    });
  }

  public sendOTP(isFromResend: boolean): void {

    if (isFromResend && this.resendRemainingTime > 0) {
      return; // Prevent resending if timer is active
    }

    this.userService.sendCode({
      "send_hash_code": "false",
      "prefix": this.selectedCountryCode.phonePrefix,
      "phoneNumber": (this.phoneNumber)?.toString(),
      "resendProvider": "sms",
      "systemInfo": {
        "deviceType": "web",
        "appVersion": 1
      }
    }).subscribe((response) => {
      this.userId = response.user;
      this.ls.store('uid', this.userId);
      if (isFromResend) {
        // A resend issues a brand-new code, so the expiry countdown has to start
        // over — otherwise the "code expired" message stays on screen while the
        // code the user just asked for is perfectly valid. Clear the old
        // interval first: resend is allowed from 25s, when the 120s timer is
        // still running, and a second interval would count down twice as fast.
        clearInterval(this.timer);
        this.remainingTime = 120;
        this.updateDisplayTime();
        this.startTimer();

        this.resendRemainingTime = 25; // Reset resend timer
        this.updateResendDisplayTime();
        this.startResendTimer();
      }
    });
  }

  startTimer() {
    this.timer = setInterval(() => {
      if (this.remainingTime > 0) {
        this.remainingTime--;
        this.updateDisplayTime();
      } else {
        clearInterval(this.timer); // Stop the timer when it reaches 0
      }
    }, 1000);
  }

  startResendTimer() {
    this.resendTimer = setInterval(() => {
      if (this.resendRemainingTime > 0) {
        this.resendRemainingTime--;
        this.updateResendDisplayTime();
        // console.log('Remaining Time:', this.resendDisplayTime);
      } else {
        clearInterval(this.resendTimer); // Stop the timer when it reaches 0
      }
    }, 1000);
  }


  updateResendDisplayTime() {
    const minutes = Math.floor(this.resendRemainingTime / 60);
    const seconds = this.resendRemainingTime % 60;
    this.resendDisplayTime = `${this.padZero(minutes)}:${this.padZero(seconds)}`;
    this.cd.detectChanges();
  }

  updateDisplayTime() {
    const minutes = Math.floor(this.remainingTime / 60);
    const seconds = this.remainingTime % 60;
    this.displayTime = `${this.padZero(minutes)}:${this.padZero(seconds)}`;
    this.cd.detectChanges();
    //console.log('Display Time Updated:', this.displayTime);
  }

  padZero(value: number): string {
    return value < 10 ? '0' + value : value.toString();
  }

  public clearTimers(): void {
    if (this.timer) {
      clearInterval(this.timer);
      clearInterval(this.resendTimer);
      this.remainingTime = 120;
      this.resendRemainingTime = 25;
      this.updateDisplayTime();
      this.updateResendDisplayTime();
    }
  }

  isSubmitting = false;

  createOrder() {
    if (this.isSubmitting) return;
    // Without a verified phone there is no Parse session, so the backend cannot
    // set the order's required `user` pointer and the save would fail
    if (!this.otpVerified) {
      this.toastr.error(this.translate.instant('journey_toast_verify_first'));
      this.goToStep(5);
      return;
    }
    this.isSubmitting = true;
    const orderData = {
      spaceType: this.selectedSpaceType,
      size: this.selectedSize,
      spaceDetails: this.collectSpaceDetails(),
      automations: this.selectedAutomations,
      countryPhonePrefix: this.selectedCountryCode.phonePrefix,
      phoneNumber: this.phoneNumber,
      email: this.email
    }

    this.backendService.createOrder(orderData).subscribe({
      next: (response) => {
        this.goToStep(7);
      },
      error: (err) => {
        this.isSubmitting = false;
        console.error('Error creating order:', err);
        this.toastr.error(this.translate.instant('journey_toast_order_failed'));
      }
    });
  }
}
